<template>
  <a-modal
    title="Configure AI models"
    :visible="visible"
    :width="720"
    :confirmLoading="saving"
    :okButtonProps="{ props: { disabled: !configReady || saving } }"
    okText="Save"
    cancelText="Cancel"
    :bodyStyle="{ maxHeight: '70vh', overflowY: 'auto' }"
    @ok="save"
    @cancel="close"
  >
    <div
      v-if="loading"
      class="d-flex justify-content-center align-items-center pt-35-px"
    >
      <Spinner />
    </div>

    <template v-else-if="configReady">
      <div
        v-for="scope in scopes"
        :key="scope.key"
        class="field"
      >
        <div class="field__label">
          {{ scope.label }}
        </div>
        <a-select
          v-model="scopeModels[scope.key]"
          style="width: 100%"
          :disabled="saving"
        >
          <a-select-option :value="nullModel">
            Default
          </a-select-option>
          <a-select-option
            v-for="model in models"
            :key="model.model_key"
            :value="model.model_key"
          >
            {{ modelLabel(model) }}
          </a-select-option>
        </a-select>
      </div>

      <div class="field">
        <div class="field__label">
          Rerun model
        </div>
        <div class="field__hint">
          Reruns MEDIUM and HIGH alerts on this model
        </div>
        <a-select
          v-model="rerunModel"
          style="width: 100%"
          :disabled="saving"
        >
          <a-select-option :value="nullModel">
            No rerun
          </a-select-option>
          <a-select-option
            v-for="model in models"
            :key="model.model_key"
            :value="model.model_key"
          >
            {{ modelLabel(model) }}
          </a-select-option>
        </a-select>
      </div>

      <div class="field">
        <div class="field__label">
          AI asset analysis (JavaScript and CSS)
        </div>
        <a-switch
          v-model="aiAssetEnabled"
          :disabled="saving"
        />
      </div>
    </template>
  </a-modal>
</template>

<script>
  import AdminService from '@modules/Admin/services/admin.service';
  import { getApiErrorMessage } from '@modules/Policy/consts/RenderingConfig.consts';

  // a-select does not accept null as an option value.
  const NULL_MODEL = '__null__';

  const SCOPES = [
    { key: 'html', label: 'HTML' },
    { key: 'vcd_thumbs', label: 'VCD thumbnails' },
    { key: 'current_screenshot', label: 'Current screenshot' },
    { key: 'compare_screenshots', label: 'Compare screenshots' },
    { key: 'compare_screenshots_previous', label: 'Compare with previous screenshot' },
    { key: 'javascript', label: 'JavaScript' },
    { key: 'css', label: 'CSS' },
  ];

  function emptyScopeModels() {
    const models = {};
    SCOPES.forEach((scope) => {
      models[scope.key] = NULL_MODEL;
    });
    return models;
  }

  function toSelectValue(modelKey) {
    if (modelKey == null) {
      return NULL_MODEL;
    }
    return modelKey;
  }

  function toApiModelKey(selectValue) {
    if (selectValue == null || selectValue === NULL_MODEL) {
      return null;
    }
    return selectValue;
  }

  export default {
    name: 'AiTriageModelConfigModal',
    props: {
      visible: {
        type: Boolean,
        default: false,
      },
      customerId: {
        type: [Number, String],
        required: true,
      },
    },
    data() {
      return {
        scopes: SCOPES,
        nullModel: NULL_MODEL,
        loading: false,
        configReady: false,
        saving: false,
        // Bumped on each open so a response from the previous open cannot
        // repopulate the form after it was reset.
        loadToken: 0,
        models: [],
        scopeModels: emptyScopeModels(),
        rerunModel: NULL_MODEL,
        aiAssetEnabled: false,
      };
    },
    watch: {
      // The lazily loaded modal can mount with visible already true.
      visible: {
        immediate: true,
        handler(isVisible) {
          if (isVisible) {
            this.openAndLoad();
          }
        },
      },
    },
    methods: {
      modelLabel(model) {
        return `${model.model_key} (${model.model_id})`;
      },
      isCurrentLoad(token) {
        return token === this.loadToken && this.visible;
      },
      openAndLoad() {
        const token = this.loadToken + 1;
        this.loadToken = token;
        this.loading = true;
        this.configReady = false;
        this.models = [];
        this.scopeModels = emptyScopeModels();
        this.rerunModel = NULL_MODEL;
        this.aiAssetEnabled = false;
        this.fetchConfig(token);
      },
      async fetchConfig(token) {
        const customerId = this.customerId;
        try {
          const [models, config] = await Promise.all([
            AdminService.fetchAiTriageModels(),
            AdminService.fetchCustomerAiTriageModelConfig(customerId),
          ]);
          if (!this.isCurrentLoad(token)) {
            return;
          }
          this.models = (Array.isArray(models) ? models : []).filter((model) => model && model.model_key);
          this.applyConfig(config);
          this.configReady = true;
        } catch (e) {
          if (this.isCurrentLoad(token)) {
            this.$showErrorMessage(getApiErrorMessage(e, 'Failed to load AI triage model config.'));
          }
        } finally {
          if (this.isCurrentLoad(token)) {
            this.loading = false;
          }
        }
      },
      applyConfig(config) {
        const scopes = (config && config.scope_model_keys) || {};
        const next = emptyScopeModels();
        SCOPES.forEach((scope) => {
          next[scope.key] = toSelectValue(scopes[scope.key]);
        });
        this.scopeModels = next;
        this.rerunModel = toSelectValue(config && config.rerun_model_key);
        this.aiAssetEnabled = Boolean(config && config.ai_asset_enabled);
      },
      buildBody() {
        const scopeModelKeys = {};
        SCOPES.forEach((scope) => {
          scopeModelKeys[scope.key] = toApiModelKey(this.scopeModels[scope.key]);
        });
        return {
          ai_asset_enabled: Boolean(this.aiAssetEnabled),
          rerun_model_key: toApiModelKey(this.rerunModel),
          scope_model_keys: scopeModelKeys,
        };
      },
      async save() {
        if (this.saving || !this.configReady) {
          return;
        }
        const customerId = this.customerId;
        const body = this.buildBody();
        this.saving = true;
        try {
          await AdminService.updateCustomerAiTriageModelConfig(customerId, body);
          this.$showSuccessMessage('AI triage model config saved.');
          this.$emit('close');
        } catch (e) {
          this.$showMultiErrorMessage(e.messages);
        } finally {
          this.saving = false;
        }
      },
      close() {
        if (this.saving) {
          return;
        }
        this.$emit('close');
      },
    },
  };
</script>

<style scoped>
  .field {
    margin-bottom: 16px;
  }
  .field__label {
    font-size: 12px;
    line-height: 18px;
    color: #595959;
    margin-bottom: 4px;
  }
  .field__hint {
    font-size: 12px;
    line-height: 16px;
    color: #8c8c8c;
    margin-bottom: 4px;
  }
</style>
